import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ArrowBack as BackIcon } from '@material-ui/icons';
import {
  Box,
  Chip,
  Container,
  Grid,
  IconButton,
  Button,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  AttachMoney as DollarSignIcon,
  Cancel as CancelIcon
} from '@material-ui/icons';
import HeaderSkeleton from './HeaderSkeleton';
import Disposition from '../Disposition';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3, 0),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0, 0, 10, 10),
    [theme.breakpoints.down(992)]: {
      borderRadius: 0
    },
  },

  badge: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
    color: theme.palette.text.secondary.main,
  },
  badgeIcon: {
    marginRight: theme.spacing(1)
  },
  openStatus: {
    color: '#007913',
    backgroundColor: '#D4F9DC'
  },
  closedStatus: {
    marginRight: -2,
    color: '#006791',
    backgroundColor: '#D4EEF9',
  },
  status: {
    marginLeft: theme.spacing(6),
  },
  dateOfLossGridItem: {
    marginLeft: theme.spacing(1),
    marginRight: -theme.spacing(2.5)
  },
  approvedAmountGridItem: {
    marginLeft: -theme.spacing(1.5)
  },
  mobileDisplay: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(6.5)
  },
  approveButton: {
    backgroundColor: '#fff !important',
    color: theme.palette.primary.main + ' !important',
    border: '1px solid #b7bcc1 !important',
    boxShadow: 'none !important',
    '&:hover': {
      backgroundColor: '#f5fafd !important',
      color: theme.palette.primary.dark + ' !important',
      border: `1px solid ${theme.palette.primary.dark} !important`,
    }
  },
  denyButton: {
    backgroundColor: '#fff !important',
    color: '#0451ab !important', // dark blue
    border: '1px solid #b7bcc1 !important',
    boxShadow: 'none !important',
    '&:hover': {
      backgroundColor: '#d0e3f7 !important',
      color: '#033c7a !important',
      border: '1px solid #033c7a !important',
    }
  },
  [theme.breakpoints.down(992)]: {
    approvedAmountGridItem: {
      display: 'none'
    },
    amountRequested: {
      display: 'none'
    },
    dateOfLossGridItem: {
      display: 'none'
    }
  },
  [theme.breakpoints.up(992)]: {
    mobileDisplay: {
      display: 'none'
    },
  }
}));

function Header({ className, isLoading, handleClaimStatusUpdate, isGlassProcessor, claimStatus, claimDisposition, user, fromEmail, onGoBack, ...rest }) {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();


  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);


  const handleActionClick = (action) => {
    setPendingAction(action);
    setConfirmOpen(true);
  };


  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingAction(null);
  };


  const handleConfirm = () => {
    if (pendingAction) {
      handleClaimStatusUpdate(pendingAction);
    }
    setConfirmOpen(false);
    setPendingAction(null);
  };

  const getStatusLabelClass = status => {
    switch (status) {
      case 'O':
      case 'Open':
        return classes.openStatus;
      case 'C':
      case 'Close':
      case 'Closed':
        return classes.closedStatus;
      default:
        return '';
    }
  };

  const handleGoBack = () => {
    if (user?.roles?.length > 0) {
      user.roles.forEach(role => {
        if (role.name === 'Glass Claims Processor' || role.name === 'Glass Claims View Only') {
          onGoBack();
          history.push('/glass/browse-claim');
        }
        else {
          history.push('/glass/search');
        }
      });
    }
    else if (fromEmail === true) {
      onGoBack();
      history.push('/glass/search');
    }
    else {
      onGoBack();
    }
  }

  if (isLoading) return <HeaderSkeleton onGoBack={handleGoBack} />
  return (
    <Grid
      container
      className={clsx(classes.root, className)}
      direction="row"
      {...rest}
    >
      <Container maxWidth="lg" component="section">
        <Box
          display="flex"
          alignItems="center"
          color="text.secondary"
          width="100%"
        >
          <Grid container spacing={4} alignItems="center" direction="row">
            <Grid item>
              <IconButton
                onClick={handleGoBack}
                className={classes.backButton}
                edge="start"
              >
                <BackIcon />
              </IconButton>
              <Chip
                className={clsx(classes.status, getStatusLabelClass(claimStatus))}
                label={claimStatus}
              />
              {<Disposition claimStatus={claimStatus} disposition={claimDisposition} />}
            </Grid>
            {isGlassProcessor && claimStatus === 'Open' && (
              <Grid item xs>
                <Box display="flex" justifyContent="flex-end" alignItems="center">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleActionClick('Approved')}
                    style={{ marginRight: 16 }}
                    startIcon={<DollarSignIcon size={20} />}
                    className={classes.approveButton}
                  >
                    {t('glassClaimActions.approve')}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleActionClick('Denied')}
                    startIcon={<CancelIcon size={20} style={{ color: '#0451ab' }} />}
                    className={classes.denyButton}
                  >
                    {t('glassClaimActions.deny')}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
        {confirmOpen && (
          <Dialog
            open={confirmOpen}
            onClose={handleCancel}
            aria-labelledby="claim-action-confirm-dialog"
            maxWidth="xs"
            fullWidth
          >
            <DialogContent>
              <DialogContentText>
                {pendingAction === 'Approved'
                  ? t('glassClaimDialog.confirmApprove')
                  : pendingAction === 'Denied'
                    ? t('glassClaimDialog.confirmDeny')
                    : ''}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancel} color="default" variant="outlined">
                {t('glassClaimDialog.cancel')}
              </Button>
              <Button onClick={handleConfirm} color="primary" variant="contained" autoFocus>
                {t('glassClaimDialog.confirm')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
        <Box display="flex" alignItems="center" >

        </Box>
      </Container>
    </Grid>
  );
}

Header.propTypes = {
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  handleClaimStatusUpdate: PropTypes.func.isRequired,
  isGlassProcessor: PropTypes.bool,
  claimStatus: PropTypes.string.isRequired,
  claimDisposition: PropTypes.string,
  user: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
    })),
    email: PropTypes.string,
  }),
  fromEmail: PropTypes.bool,
  onGoBack: PropTypes.func.isRequired,
};

Header.defaultProps = {
  isGlassProcessor: false,
  claimDisposition: '',
  user: undefined,
  fromEmail: false,
};

export default Header;
